/**
 * The queue of notifications waiting for the user to say "Noted".
 *
 * Desk, 2026-09-27: "any notification coming should be seen at the centre of the
 * page so the user understands what it is… and give a Noted button." A toast that
 * left after 4 seconds meant a message about a failed payment or an unbridged
 * lab charge was gone before it could be read, and left nothing to look back on.
 *
 * ONE AT A TIME, IN ORDER. Several can arrive together (a save that raises three
 * problems). Stacking dialogs would hide all but the top one; a queue shows each
 * in full and says how many are waiting.
 *
 * IDENTICAL ONES COLLAPSE. A double-click that fires the same failure twice is
 * one message shown "2 times", not two clicks of "Noted" for the same news.
 *
 * Distinct from `notification.store.ts`, which is the bell's history: this is what
 * is on screen right now and needs an answer. Acknowledging here marks the
 * matching bell entries read.
 */
import { create } from 'zustand';
import { useNotificationStore, type NotificationSeverity } from './notification.store';

export interface PendingNotification {
  id: string;
  severity: NotificationSeverity;
  title: string;
  message?: string;
  /** What the person should do about it. Absent → the dialog supplies a sensible default for the severity. */
  nextStep?: string;
  /** ISO time of the FIRST occurrence — the time a user quotes when reporting it. */
  at: string;
  /** How many times this identical notification has arrived while waiting. */
  count: number;
  /** Bell entries to mark read once acknowledged. */
  bellIds: string[];
}

interface AckState {
  queue: PendingNotification[];
  enqueue: (item: Omit<PendingNotification, 'id' | 'at' | 'count' | 'bellIds'> & { bellId?: string }) => void;
  /** "Noted" — dismisses the one on screen. */
  acknowledge: () => void;
  /** "Noted all" — dismisses everything waiting. */
  acknowledgeAll: () => void;
}

const markBellRead = (ids: string[]): void => {
  const { markRead } = useNotificationStore.getState();
  ids.forEach(markRead);
};

export const useNotificationAckStore = create<AckState>()((set, get) => ({
  queue: [],

  enqueue: ({ bellId, ...item }) =>
    set((state) => {
      const same = state.queue.find((q) => q.severity === item.severity && q.title === item.title && q.message === item.message);
      if (same) {
        return {
          queue: state.queue.map((q) =>
            q === same ? { ...q, count: q.count + 1, bellIds: bellId ? [...q.bellIds, bellId] : q.bellIds } : q,
          ),
        };
      }
      const entry: PendingNotification = {
        ...item,
        id: crypto.randomUUID(),
        at: new Date().toISOString(),
        count: 1,
        bellIds: bellId ? [bellId] : [],
      };
      return { queue: [...state.queue, entry] };
    }),

  acknowledge: () => {
    const [first, ...rest] = get().queue;
    if (!first) return;
    markBellRead(first.bellIds);
    set({ queue: rest });
  },

  acknowledgeAll: () => {
    markBellRead(get().queue.flatMap((q) => q.bellIds));
    set({ queue: [] });
  },
}));
